import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomerProfileDto } from '../../../services/customer-api.service';
import { AuthService } from '../../../services/auth.service';

type CustomerPageMode = 'new' | 'edit' | 'view';

/**
 * The single routed page for the customer profile use case. New, edit, and
 * view differ by mode; they do not own separate workspace page shells.
 */
@Component({
  selector: 'app-customer-page',
  templateUrl: './customer-page.component.html',
  styleUrls: ['./customer-page.component.css'],
  host: { class: 'workspace-page', '[class.workspace-page--full]': '!inline' },
})
export class CustomerPageComponent implements OnInit, OnDestroy {
  @Input() inline = false;
  // A cached profile can arrive during child initialization. Notify the list
  // after its current check, rather than changing the already-rendered summary.
  @Output() profileLoaded = new EventEmitter<CustomerProfileDto>(true);
  mode: CustomerPageMode;
  readonly returnToCollection: boolean;

  @Input() customerId: number | null = null;
  private readonly destroyed$ = new Subject<void>();

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Customer detail'), name: 'Customer detail', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ customerId: this.customerId, mode: this.mode }),
      describe: state => JSON.stringify(state),
    });
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly auth: AuthService,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {
    this.mode = route.snapshot.data['customerPageMode'] as CustomerPageMode;
    this.returnToCollection = route.parent?.routeConfig?.path === 'table';
  }

  ngOnInit(): void {
    this.registerViewContext();
    if (this.inline) { this.mode = 'view'; return; }
    this.route.paramMap
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        const id = Number(params.get('id'));
        this.customerId = Number.isInteger(id) && id > 0 ? id : null;
      });
  }

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get title(): string {
    if (this.mode === 'new') return 'New Customer';
    if (this.mode === 'edit') return 'Edit Customer';
    return 'View Customer';
  }

  get isView(): boolean {
    return this.mode === 'view';
  }

  back(): void {
    this.router.navigate(['/customers']);
  }

  edit(): void {
    if (this.inline) {
      if (this.auth.hasPermission('Customer.Manage')) this.mode = 'edit';
      return;
    }
    if (this.customerId !== null) {
      this.router.navigate(this.returnToCollection
        ? ['/customers/table', this.customerId, 'edit']
        : ['/customers', this.customerId, 'edit']);
    }
  }
}
